// Mutation confirm cards (P1, docs/design/2026-08-05-agent-system-admin-design.md).
// The model proposes writes as an ```agent-action JSON block; each entry renders
// as a card and NOTHING happens until the human clicks Confirm — the click calls
// the same audited admin REST endpoints the UI forms use, with the human's JWT.
// The model is never in the write path. Any parse failure yields no panel.

import { useState } from "react";
import { Icon } from "../design/ui";
import {
  createFollowupApi,
  createProjectAction,
  createProjectActivity,
  escalateActionToJira,
  resolveFollowupApi,
  saveProject,
  updateProjectAction,
  type ActionCreateInput,
  type Project,
} from "../lib/brain-api";
import "./agent.css";

export interface AgentActionProposal {
  op: string;
  summary: string;
  args: Record<string, unknown>;
}

// Which arg carries the human-editable text per op (shown as a textarea).
const EDITABLE_KEY: Record<string, string> = {
  add_action: "text",
  update_action: "text",
  escalate_action_to_jira: "summary",
  add_followup: "text",
  resolve_followup: "resolution",
  log_activity: "text",
  upsert_project: "description",
};

const OP_LABEL: Record<string, string> = {
  add_action: "Add action",
  update_action: "Update action",
  escalate_action_to_jira: "Raise Jira issue",
  add_followup: "Add followup",
  resolve_followup: "Resolve followup",
  log_activity: "Log activity",
  upsert_project: "Save project",
};

function nzToday(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Pacific/Auckland" });
}

/** Extract the first ```agent-action fenced block as proposals, or null. */
export function parseAgentActions(text: string): AgentActionProposal[] | null {
  const m = /```agent-action\s*([\s\S]*?)```/.exec(text);
  if (!m) return null;
  try {
    const arr = JSON.parse(m[1].trim());
    if (
      Array.isArray(arr) &&
      arr.length > 0 &&
      arr.every(
        (p) =>
          p &&
          typeof p.op === "string" &&
          OP_LABEL[p.op] &&
          typeof p.summary === "string" &&
          p.args &&
          typeof p.args === "object",
      )
    ) {
      return arr.slice(0, 3) as AgentActionProposal[];
    }
  } catch {
    /* malformed — no panel */
  }
  return null;
}

/** Perform one confirmed op via the normal admin REST client. Returns a result
 *  line, plus an external href for ops that create something linkable. */
async function runOp(op: string, args: Record<string, unknown>): Promise<{ done: string; href?: string }> {
  switch (op) {
    case "add_action": {
      const a = await createProjectAction({ ...args, kind: "action" } as ActionCreateInput);
      return { done: `Action added${a.projectId ? ` to ${a.projectId}` : ""}` };
    }
    case "update_action": {
      const { id, ...patch } = args;
      await updateProjectAction(String(id), patch as Parameters<typeof updateProjectAction>[1]);
      return { done: "Action updated" };
    }
    case "escalate_action_to_jira": {
      const { id, ...body } = args;
      const out = await escalateActionToJira(String(id), body as Parameters<typeof escalateActionToJira>[1]);
      return { done: `Raised ${out.key}`, href: out.url };
    }
    case "add_followup": {
      const f = await createFollowupApi(args as { text: string; projectId?: string; customerId?: string });
      return { done: `Followup added${f.projectId ? ` to ${f.projectId}` : ""}` };
    }
    case "resolve_followup": {
      const { id, outcome, resolution } = args as { id: string; outcome: string; resolution?: string };
      await resolveFollowupApi(String(id), outcome as "done" | "dropped" | "superseded", resolution);
      return { done: `Followup ${outcome}` };
    }
    case "log_activity": {
      await createProjectActivity({
        ...(args as { projectId: string; text: string }),
        date: typeof args.date === "string" ? args.date : nzToday(),
      });
      return { done: "Activity logged" };
    }
    case "upsert_project": {
      const p = await saveProject(args as Partial<Project> & { projectId: string });
      return { done: `Project ${p.projectId} saved` };
    }
    default:
      throw new Error(`Unknown op: ${op}`);
  }
}

type RowState = {
  proposal: AgentActionProposal;
  text: string; // editable primary field
  busy?: boolean;
  done?: string;
  href?: string;
  error?: string;
  dismissed?: boolean;
};

export function ActionPanel({ proposals }: { proposals: AgentActionProposal[] }) {
  const [rows, setRows] = useState<RowState[]>(
    proposals.map((p) => ({ proposal: p, text: String(p.args[EDITABLE_KEY[p.op]] ?? "") })),
  );

  const patch = (i: number, next: Partial<RowState>) =>
    setRows((r) => r.map((row, j) => (j === i ? { ...row, ...next } : row)));

  const confirm = async (i: number) => {
    const row = rows[i];
    patch(i, { busy: true, error: undefined });
    const key = EDITABLE_KEY[row.proposal.op];
    const args = row.text.trim() ? { ...row.proposal.args, [key]: row.text.trim() } : row.proposal.args;
    try {
      const out = await runOp(row.proposal.op, args);
      patch(i, { busy: false, done: out.done, href: out.href });
    } catch (e) {
      patch(i, { busy: false, error: e instanceof Error ? e.message : String(e) });
    }
  };

  return (
    <div className="agent-proposals">
      <div className="agent-proposals-head">
        <Icon name="bolt" size={13} /> Proposed changes — nothing happens until you confirm
      </div>
      {rows.map((row, i) => {
        if (row.dismissed) return null;
        const { op, summary, args } = row.proposal;
        const editKey = EDITABLE_KEY[op];
        const detail = Object.entries(args).filter(([k, v]) => k !== editKey && v !== undefined && v !== null);
        return (
          <div key={i} className="agent-proposal">
            <div className="agent-proposals-head">
              <Icon name={op === "escalate_action_to_jira" ? "ticket" : "pencil"} size={12} />
              {OP_LABEL[op]}
              {op === "escalate_action_to_jira" && " · creates a real Jira issue"}
            </div>
            {row.done ? (
              <div className="agent-proposal-saved">
                <Icon name="check" size={13} /> {row.done}
                {row.href && (
                  <>
                    {" · "}
                    <a href={row.href} target="_blank" rel="noopener noreferrer">open</a>
                  </>
                )}
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12.5 }}>{summary}</div>
                <textarea
                  className="agent-proposal-text"
                  value={row.text}
                  rows={2}
                  onChange={(e) => patch(i, { text: e.target.value })}
                  aria-label={`${OP_LABEL[op]} text`}
                />
                {detail.length > 0 && (
                  <div style={{ fontSize: 11.5, opacity: 0.75, fontFamily: "monospace" }}>
                    {detail.map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" · ")}
                  </div>
                )}
                <div className="agent-proposal-foot">
                  <button
                    type="button"
                    className="agent-proposal-save"
                    disabled={row.busy}
                    onClick={() => void confirm(i)}
                  >
                    {row.busy ? "Working…" : "Confirm"}
                  </button>
                  <button
                    type="button"
                    className="agent-proposal-select"
                    disabled={row.busy}
                    onClick={() => patch(i, { dismissed: true })}
                  >
                    Dismiss
                  </button>
                </div>
                {row.error && <div className="agent-error">{row.error}</div>}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
